import React from "react";
import { useTranslation } from "react-i18next";
import { CircularProgress } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation } from "@fortawesome/free-solid-svg-icons";
import { DialogFrame } from "../../../components/Dialogs/dialog-base";
import { PrimaryButton } from "../../../components/Buttons/PrimaryButton";
import { SecondaryButton } from "../../../components/Buttons/SecondaryButton";
import { BaseLogo } from "../../../../core/models/entities/BaseLogo";

/**
 * Multi-delete for the LOGO list (flag REACT_APP_MULTI_DELETE_LOGOS_ENABLED).
 *
 * Mirrors the design list's multi-delete (multi-delete-dialogs.component.tsx,
 * Nichlas' explicit requirement 19/8: deleting several at once MUST ask
 * TWICE). Step 1 is the neutral "what will happen" dialog (list of the logos
 * that WILL be deleted + how many are skipped). Step 2 is the visibly sharper
 * one - red border, red confirm button, "cannot be undone" - and only ITS
 * confirm button actually deletes. Cancel/close before that deletes nothing.
 *
 * Purely presentational; the deletable/skipped split and the sequential
 * deletion live in useFunctionalListLogosViewModel so the rules stay next to
 * the single-delete they mirror (the logo popup's DELETE LOGO button).
 */
export interface MultiDeleteLogosDialogsProps {
  /** 0 = closed, 1 = first (neutral) dialog, 2 = final (red) dialog. */
  step: 0 | 1 | 2;
  /** The selected logos that WILL be deleted (rules already applied). */
  deletable: BaseLogo[];
  /** Selected logos that are skipped (chain logos cannot be deleted). */
  skippedCount: number;
  /** True while the sequential deletion runs - locks every escape hatch. */
  isDeleting: boolean;
  /** Completed deletions so far (for the progress label on the button). */
  progress: number;
  onCancel: () => void;
  onContinue: () => void;
  onConfirm: () => void;
}

export function MultiDeleteLogosDialogs({
  step,
  deletable,
  skippedCount,
  isDeleting,
  progress,
  onCancel,
  onContinue,
  onConfirm,
}: MultiDeleteLogosDialogsProps) {
  const { t } = useTranslation();
  const count = deletable.length;

  // DialogFrame's chrome (incl. the corner X) closes via closeDialog - route
  // it to cancel, but make it a no-op mid-deletion so the run can't be
  // "closed away" half-done.
  const guardedCancel = () => {
    if (!isDeleting) onCancel();
  };

  const dialog1Vm = {
    isOpen: step === 1,
    openDialog: () => {},
    closeDialog: guardedCancel,
  };
  const dialog2Vm = {
    isOpen: step === 2,
    openDialog: () => {},
    closeDialog: guardedCancel,
  };

  return (
    <>
      {/* Dialog 1: neutral confirmation with the exact list of what happens */}
      <DialogFrame dialogOption={dialog1Vm} onClose={guardedCancel}>
        <div className={"flex flex-col space-y-4"}>
          <div className={"flex justify-center"}>
            <FontAwesomeIcon size={"3x"} icon={faTriangleExclamation} />
          </div>
          <p className={"text-center font-bold"}>
            {t("logoList.multiDeleteTitle", { count })}
          </p>
          {skippedCount > 0 && (
            <p className={"text-center text-xs text-gray-600"}>
              {t("logoList.multiDeleteSkipLine", {
                deletable: count,
                skipped: skippedCount,
              })}
            </p>
          )}
          <div
            className={
              "max-h-60 overflow-y-auto rounded-md border border-[#ECEEF9] divide-y divide-[#ECEEF9]"
            }
          >
            {deletable.map((logo) => (
              <div
                key={logo.id}
                className={"flex items-baseline gap-2 px-3 py-2 text-xs"}
              >
                <span className={"font-bold whitespace-nowrap"}>
                  {logo.product?.sku ?? "-"}
                </span>
                <span
                  className={
                    "text-gray-600 overflow-hidden overflow-ellipsis whitespace-nowrap"
                  }
                >
                  {logo.title}
                </span>
              </div>
            ))}
          </div>
          <div className={"flex justify-center space-x-4"}>
            <SecondaryButton width={"sm"} onClick={guardedCancel}>
              {t("cancel")}
            </SecondaryButton>
            <PrimaryButton width={"sm"} onClick={onContinue}>
              {t("logoList.multiDeleteContinue")}
            </PrimaryButton>
          </div>
        </div>
      </DialogFrame>

      {/* Dialog 2: the visibly sharper one - red border/button, executes */}
      <DialogFrame dialogOption={dialog2Vm} onClose={guardedCancel}>
        <div
          className={
            "flex flex-col space-y-4 rounded-lg border-2 border-[#E22F2F] p-4"
          }
        >
          <div className={"flex justify-center text-[#E22F2F]"}>
            <FontAwesomeIcon size={"3x"} icon={faTriangleExclamation} />
          </div>
          <p className={"text-center font-bold text-[#E22F2F]"}>
            {t("logoList.multiDeleteFinalTitle")}
          </p>
          <p className={"text-center whitespace-pre-line"}>
            {t("logoList.multiDeleteFinalWarning", { count })}
          </p>
          <div className={"flex justify-center space-x-4"}>
            <SecondaryButton
              width={"sm"}
              onClick={guardedCancel}
              disabled={isDeleting}
            >
              {t("cancel")}
            </SecondaryButton>
            {/* Solid red confirm; disabled + spinner while running is the
                double-click guard - a second click can never start a second
                run. */}
            <button
              className={
                "button-root px-4 bg-[#E22F2F] text-white border border-[#E22F2F] hover:brightness-95"
              }
              onClick={onConfirm}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <span className={"flex items-center gap-2"}>
                  <CircularProgress size={14} sx={{ color: "white" }} />
                  {t("logoList.multiDeleteDeleting", {
                    done: progress,
                    total: count,
                  })}
                </span>
              ) : (
                t("logoList.multiDeleteConfirm", { count })
              )}
            </button>
          </div>
        </div>
      </DialogFrame>
    </>
  );
}
